import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, ArrowPathIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import Button from 'components/Button/Button'
import { connect } from 'react-redux'
import { regenerateQR } from 'store/actions/auth'
import { getApiErrorMessage, getApiErrorMessageSync, post } from 'api'
import { USER_FACING_ERRORS } from 'constants/userFacingErrors'
import { toast } from 'react-toastify'

const QRCodeModal = ({ open, setOpen, user, regenerateQR }) => {
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const handleRegenerate = async () => {
        setLoading(true);
        try {
            await regenerateQR()
            toast.success("QR Code regenerated successfully")
        } catch (error) {
            toast.error(getApiErrorMessageSync(error, USER_FACING_ERRORS.QR_REGENERATE_FAILED))
        } finally {
            setLoading(false);
        }
    }

    const handleDownload = async () => {
        if (!user.qrCodeUrl) return;
        setDownloading(true);
        try {
            const response = await post({
                subUrl: '/user/download-qr',
                data: { url: user.qrCodeUrl, uid: user.uid },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'image/png' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const filename = `qr-code-${user.firstName || 'user'}-${user.lastName || ''}.png`;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("QR Code downloaded successfully");
        } catch (error) {
            console.error('Download failed:', error);
            const msg = await getApiErrorMessage(error, USER_FACING_ERRORS.QR_DOWNLOAD_FAILED);
            toast.error(msg);
        } finally {
            setDownloading(false);
        }
    }

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[2000]" onClose={setOpen}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-6">
                                <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
                                    <button
                                        type="button"
                                        className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                        onClick={() => setOpen(false)}
                                    >
                                        <span className="sr-only">Close</span>
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>
                                <div>
                                    <div className="mt-3 text-center sm:mt-5">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            My QR Code
                                        </Dialog.Title>
                                        <div className="mt-4 flex justify-center">
                                            {user.qrCodeUrl ? (
                                                <img src={user.qrCodeUrl} alt="My QR Code" className="w-48 h-48 object-contain border rounded-lg" />
                                            ) : (
                                                <div className="w-48 h-48 flex items-center justify-center border rounded-lg bg-gray-50 text-gray-400 text-sm">
                                                    No QR Code
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-5 sm:mt-6 flex gap-3">
                                    {user.qrCodeUrl && (
                                        <Button
                                            type="button"
                                            loading={downloading}
                                            disabled={downloading}
                                            className="!w-full justify-center !py-2 flex items-center gap-2 bg-secondary text-white border-secondary hover:bg-opacity-90 hover:border-secondary"
                                            onClick={handleDownload}
                                        >
                                            <ArrowDownTrayIcon className="h-5 w-5" />
                                            Download QR Code
                                        </Button>
                                    )}
                                    <Button
                                        type="button"
                                        loading={loading}
                                        disabled={loading}
                                        className="!w-full justify-center !py-2 flex items-center gap-2 bg-secondary text-white border-secondary hover:bg-opacity-90 hover:border-secondary"
                                        onClick={handleRegenerate}
                                    >
                                        <ArrowPathIcon className="h-5 w-5" />
                                        {user.qrCodeUrl ? 'Regenerate QR Code' : 'Generate QR Code'}
                                    </Button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}

const mapStateToProps = ({ auth }) => ({
    user: auth.user
})

export default connect(mapStateToProps, { regenerateQR })(QRCodeModal)
